import classcat from "classcat";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";

import { TranslationContext } from "@is/constants";
import { ViewStateContext } from "@is/contexts";
import { HTWIContentWidget } from "@is/types";
import { getHTWISortedMedia } from "@is/utils";

import HTWIItemThumbnail from "./HTWIItemThumbnail";
import HTWIQuickAddToBasket from "./HTWIQuickAddToBasket";
import css from "./HTWIWidget.module.css";
import HTWIWidgetItem from "./HTWIWidgetItem";

type Props = {
  widget: HTWIContentWidget;
  mainVariantId?: string;
  onPDPItemClick?: () => void;
  containerRef: React.RefObject<HTMLDivElement>;
  contentItemId?: string;
  onShowMore?: (id: string) => void;
  autoCarousel?: boolean;
};

const HTWIWidget = ({
  widget,
  mainVariantId,
  onPDPItemClick,
  containerRef,
  contentItemId,
  onShowMore,
  autoCarousel,
}: Props) => {
  const { t } = useTranslation(TranslationContext.ProductDetails);
  const { quickAddToBasketItem } = useContext(ViewStateContext).content;

  const sliderRef = useRef(null);
  const thumbnailsRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const items = useMemo(
    () =>
      widget.items.map((item) => ({
        id: uuid(),
        ...item,
        thumbnailMedia: getHTWISortedMedia(item.media).splice(0, 4),
      })),
    [widget.items]
  );

  const showQuickAddToBasket = useMemo(() => {
    return (
      quickAddToBasketItem &&
      widget.items.some((widgetItem) =>
        widgetItem.items.some(
          (item) =>
            item.widgetProductItemId ===
            quickAddToBasketItem?.widgetProductItemId
        )
      )
    );
  }, [quickAddToBasketItem, widget.items]);

  const onBasketAdded = useCallback(() => {
    if (onShowMore) {
      onShowMore(widget.id);
    }
  }, [onShowMore, widget]);

  const handleScroll = useCallback(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    const scrollLeft = slider.scrollLeft;
    const sliderWidth = slider.offsetWidth;
    const scrollWidth = slider.scrollWidth;

    if (scrollLeft + sliderWidth >= scrollWidth - 1) {
      setActiveIndex(items.length - 1);
      return;
    }

    let cumulativeWidth = 0;
    for (let i = 0; i < items.length; i++) {
      cumulativeWidth += slider.children[i].offsetWidth;
      if (scrollLeft < cumulativeWidth) {
        setActiveIndex(i);
        break;
      }
    }
  }, [items.length]);

  const handleThumbnailClick = useCallback(
    (index: number) => {
      const slider = sliderRef.current;
      if (!slider) return;

      const gap = 20;
      let scrollLeft = 0;

      for (let i = 0; i < index; i++) {
        scrollLeft += slider.children[i].offsetWidth + gap;
      }

      const sliderWidth = slider.offsetWidth;
      const lastSlideWidth = slider.children[index].offsetWidth;
      if (index === items.length - 1 && lastSlideWidth < sliderWidth) {
        scrollLeft = slider.scrollWidth - sliderWidth;
      }

      slider.scrollTo({ left: scrollLeft, behavior: "smooth" });
      setActiveIndex(index);

      containerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    },
    [items.length, containerRef]
  );

  const handleDotClick = useCallback(
    (index: number) => {
      setActiveIndex(index);
      handleThumbnailClick(index);
    },
    [handleThumbnailClick]
  );

  const handleScrollToTheItem = useCallback(
    () =>
      containerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      }),
    [containerRef]
  );

  useEffect(() => {
    const thumbnails = thumbnailsRef.current;
    const thumbnail = thumbnails?.children[activeIndex];
    if (!thumbnails || !thumbnail) return;

    const thumbnailOffset = thumbnail.offsetLeft;
    const thumbnailWidth = thumbnail.offsetWidth;
    const containerWidth = thumbnails.offsetWidth;

    thumbnails.scrollTo({
      left: thumbnailOffset - (containerWidth - thumbnailWidth) / 2,
      behavior: "smooth",
    });
  }, [activeIndex]);

  useEffect(() => {
    if (!autoCarousel || items.length <= 1) return;

    const interval = setInterval(() => {
      setActiveIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % items.length;
        handleThumbnailClick(nextIndex);
        return nextIndex;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [autoCarousel, items.length, handleThumbnailClick]);

  return (
    <div
      className={classcat([
        css.container,
        { [css.autoCarousel]: autoCarousel },
      ])}
      ref={containerRef}
    >
      {!autoCarousel && (
        <div className={css.titleContainer}>
          <div className={classcat(["text-headline-3xl", css.title])}>
            {t(widget.title)}
          </div>
        </div>
      )}
      <div className={css.sliderContainer}>
        <div className={css.wrapperForQuickAddToBasket}>
          <div
            className={classcat([
              css.sliderMain,
              { [css.singleItem]: items.length === 1 },
            ])}
            ref={sliderRef}
            onScroll={handleScroll}
          >
            {items.map((item) => (
              <div
                key={item.id}
                className={classcat([
                  css.slide,
                  { [css.autoCarousel]: autoCarousel },
                ])}
              >
                <HTWIWidgetItem
                  items={item.items}
                  media={item.media}
                  title={item.name}
                  mainStyle={item.mainStyle}
                  onScrollToTheItem={handleScrollToTheItem}
                  contentItemId={contentItemId}
                  mainVariantId={mainVariantId}
                  onPDPItemClick={onPDPItemClick}
                  autoCarousel={autoCarousel}
                />
              </div>
            ))}
          </div>

          {showQuickAddToBasket && (
            <HTWIQuickAddToBasket
              item={quickAddToBasketItem}
              onBasketAdded={onBasketAdded}
            />
          )}
        </div>
        {items.length > 1 && !autoCarousel && (
          <div className={css.sliderThumbnails} ref={thumbnailsRef}>
            {items.map((item, index) => (
              <HTWIItemThumbnail
                key={item.id}
                item={item}
                index={index}
                activeIndex={activeIndex}
                onThumbnailClick={handleThumbnailClick}
              />
            ))}
          </div>
        )}
      </div>
      {items.length > 1 && autoCarousel && (
        <div className={css.dotsContainer}>
          {items.map((item, index) => (
            <button
              key={item.id}
              type="button"
              onClick={() => handleDotClick(index)}
              onTouchEnd={() => handleDotClick(index)}
            >
              <div
                className={classcat([
                  css.dot,
                  { [css.dotActive]: index === activeIndex },
                ])}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default HTWIWidget;
